import { CheckoutFormFields } from "@ecologi/react-components/src/CheckoutForm";
import {
  BusinessPlanOptions,
  Currency,
  FamilyPlanOptions,
  User,
  UserType,
} from "@ecologi/shared";
import { NextRouter, useRouter } from "next/router";
import { useMutation } from "react-query";
import { toast } from "react-toastify";
import { APIError, formatAPIErrorForView, pushPurchase } from "../../../lib";
import { useStore } from "../../../store";
import type { Store } from "../../../store/store.types";
import { createSubscriptionHandler } from "./create-subscription-handler";

export type CreateSubscriptionArgs = {
  cardElement: any;
  formValues: Omit<CheckoutFormFields, "isTermsAndConditionsAccepted">;
  selectedPlanAddons: FamilyPlanOptions | BusinessPlanOptions;
  selectedPlanId: string;
  numLocalTrees: number;
  captchaToken: string;
};

export type CreateSubscriptionHandlerArgs = Pick<
  Store,
  "location" | "stripe" | "referrerUserId" | "setUser" | "affiliateClickId"
> & { router: NextRouter; parentUsername: string | undefined };

export type CreateSubscriptionHandlerResponse = {
  user: UserType;
  paymentAmount: number;
  selectedPlanId: string;
  currency: Currency;
  referralSource: string;
  type: any;
};

export type CreateSubscriptionHandler = (
  args: CreateSubscriptionHandlerArgs & CreateSubscriptionArgs
) => Promise<CreateSubscriptionHandlerResponse>;

export const useCreateSubscription = () => {
  const router = useRouter();
  const { location, stripe, referrerUserId, setUser, affiliateClickId } =
    useStore();

  //  This route will also have an optional 'parent' query parameter which we need to send to the
  //  Create Subscription API to indicate the profile being created is for a sub-forest. If no
  //  such parameter is supplied then the profile will be a standalone profile
  const { parent } = router.query;
  let parentUsername: string | undefined;
  if (typeof parent === "string") {
    parentUsername = parent;
  } else if (Array.isArray(parent)) {
    parentUsername = parent[0];
  }

  return useMutation(
    async (data: CreateSubscriptionArgs) => {
      const args: CreateSubscriptionHandlerArgs = {
        router,
        location,
        stripe,
        referrerUserId,
        setUser,
        affiliateClickId,
        parentUsername,
      };

      return createSubscriptionHandler({ ...data, ...args });
    },
    {
      onError: (error: APIError) => {
        toast.error(formatAPIErrorForView(error));
      },
      onSuccess: ({
        user,
        paymentAmount,
        selectedPlanId,
        type,
        currency,
        referralSource,
      }) => {
        setUser(new User(user));

        const paymentAmountForReporting = paymentAmount / 100;

        if (user.customer?.processorId) {
          pushPurchase({
            transaction_id: user.customer.processorId,
            value: paymentAmountForReporting,
            currency,
            items: [
              {
                item_id: selectedPlanId,
                item_name: type,
                price: paymentAmountForReporting,
                quantity: 1,
                item_category: "subscription",
                item_brand: "Ecologi",
              },
            ],
            subscription_type: type,
            user_id: user.userId,
            referral_source: referralSource,
          });
        }

        router.push(`/thanks`);
      },
    }
  );
};
